import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { 
  FileText, 
  Copy, 
  Check,
  Lock,
  Unlock,
  TrendingUp,
  Target,
  Sparkles,
  ArrowRight,
  ChevronDown,
  Shield
} from 'lucide-react';

/**
 * RV.3: Gated Review Display
 * Shows the generated review with some sections locked.
 * Prompts user to signup to unlock full review.
 */

const GATED_FEATURES = [
  { id: 'full_achievements', label: 'All achievements with impact statements', icon: TrendingUp },
  { id: 'tailored_suggestions', label: 'Tailored improvement suggestions', icon: Target },
  { id: 'stronger_framing', label: 'Stronger impact framing', icon: Sparkles },
  { id: 'edit_capability', label: 'Edit and refine with AI', icon: FileText },
  { id: 'living_cv_save', label: 'Save to your Living CV', icon: Shield }
];

const GatedReviewDisplay = ({ 
  reviewData, 
  onUnlock, 
  onStartFresh,
  accentColor = '#dc2626' 
}) => {
  const [copied, setCopied] = useState(false);
  const [expandedAchievements, setExpandedAchievements] = useState(false);
  const navigate = useNavigate();

  const handleCopy = async () => {
    // Only copy visible content
    const visibleAchievements = reviewData.achievements
      .filter(a => a.visible)
      .map(a => `• ${a.title}\n  ${a.impact}`)
      .join('\n\n');
    
    const text = `
PERFORMANCE REVIEW DRAFT

SUMMARY
${reviewData.summary}

KEY ACHIEVEMENTS
${visibleAchievements}

[Sign up to unlock full review with all sections]
    `.trim();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleUnlock = () => {
    // Store review_id and input_hash for post-signup recovery
    const returnUrl = `/review/complete?rv_id=${reviewData.review_id}&hash=${reviewData.input_hash}`;
    
    // Store in sessionStorage as backup
    sessionStorage.setItem('rv_pending_unlock', JSON.stringify({
      review_id: reviewData.review_id,
      input_hash: reviewData.input_hash,
      flow_type: reviewData.flow_type || 'review'
    }));
    
    if (onUnlock) {
      onUnlock(returnUrl);
    } else {
      navigate(`/login?return=${encodeURIComponent(returnUrl)}`);
    }
  };

  const visibleAchievements = reviewData.achievements.filter(a => a.visible);
  const gatedAchievements = reviewData.achievements.filter(a => !a.visible);

  return (
    <div className="max-w-3xl mx-auto" data-testid="rv-gated-review">
      {/* Review Card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${accentColor}15` }}
            >
              <FileText className="w-5 h-5" style={{ color: accentColor }} />
            </div>
            <div>
              <p className="font-semibold text-slate-900">Your Performance Review</p>
              <p className="text-sm text-slate-500">Generated draft • Ready to refine</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="flex items-center gap-2"
            data-testid="rv-copy-btn"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-500" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy
              </>
            )}
          </Button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Summary */}
          <div className="mb-6">
            <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium">Summary</h4>
            <p className="text-slate-800 leading-relaxed">{reviewData.summary}</p>
          </div>

          {/* Achievements - Visible */}
          <div className="mb-6">
            <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Key Achievements
            </h4>
            
            <div className="space-y-3">
              {visibleAchievements.map((achievement, index) => (
                <div 
                  key={index}
                  className="bg-slate-50 rounded-xl p-4 border border-slate-100"
                  data-testid={`rv-achievement-${index}`}
                >
                  <p className="font-medium text-slate-900 mb-1">{achievement.title}</p>
                  <p className="text-sm text-slate-600 mb-2">{achievement.impact}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {achievement.skills.map((skill, i) => (
                      <span 
                        key={i}
                        className="px-2 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-600"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gated Section */}
          <div className="relative">
            {/* Blurred gated achievements */}
            {gatedAchievements.length > 0 && (
              <div className="space-y-3 mb-4">
                {gatedAchievements.slice(0, 2).map((achievement, index) => (
                  <div 
                    key={index}
                    className="bg-slate-50 rounded-xl p-4 border border-slate-100 relative overflow-hidden"
                  >
                    <div className="blur-sm select-none">
                      <p className="font-medium text-slate-900 mb-1">{achievement.title}</p>
                      <p className="text-sm text-slate-600 mb-2">
                        {achievement.impact.substring(0, 50)}...
                      </p>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/60 to-white/90 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Development Areas - Partial */}
            <div className="mb-4">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium flex items-center gap-2">
                <Target className="w-4 h-4" />
                Development Areas
              </h4>
              <div className="space-y-2">
                {reviewData.development_areas.items.map((area, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-slate-400 mt-0.5">•</span>
                    <span>{area}</span>
                  </div>
                ))}
                {reviewData.development_areas.gated_count > 0 && (
                  <div className="flex items-center gap-2 text-sm text-slate-400 blur-sm select-none">
                    <Lock className="w-3 h-3" />
                    <span>+{reviewData.development_areas.gated_count} more areas locked</span>
                  </div>
                )}
              </div>
            </div>

            {/* Goals - Partial */}
            <div className="mb-6">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Goals for Next Period
              </h4>
              <div className="space-y-2">
                {reviewData.goals.items.map((goal, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-slate-400 mt-0.5">•</span>
                    <span>{goal}</span>
                  </div>
                ))}
                {reviewData.goals.gated_count > 0 && (
                  <div className="flex items-center gap-2 text-sm text-slate-400 blur-sm select-none">
                    <Lock className="w-3 h-3" />
                    <span>+{reviewData.goals.gated_count} more goals locked</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Unlock CTA Section */}
        <div className="px-6 py-6 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
          <div className="text-center mb-6">
            <Unlock className="w-8 h-8 mx-auto mb-3 text-white/80" />
            <h3 className="text-lg font-semibold mb-2">Unlock Your Full Review</h3>
            <p className="text-slate-300 text-sm">
              Create a free account to access all features and save your review
            </p>
          </div>

          {/* Features to unlock */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {GATED_FEATURES.slice(0, 4).map((feature) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={feature.id}
                  className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg text-sm"
                >
                  <Icon className="w-4 h-4 text-white/70 flex-shrink-0" />
                  <span className="text-white/90 text-xs">{feature.label}</span>
                </div>
              );
            })}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleUnlock}
              className="flex-1 py-3 text-white font-medium"
              style={{ backgroundColor: accentColor }}
              data-testid="rv-unlock-btn"
            >
              <Unlock className="w-4 h-4 mr-2" />
              Unlock Full Review (Free)
            </Button>
            
            {onStartFresh && (
              <Button
                variant="outline"
                onClick={onStartFresh}
                className="flex-1 py-3 text-white border-white/30 hover:bg-white/10"
                data-testid="rv-start-fresh-btn"
              >
                Start Fresh
              </Button>
            )}
          </div>

          <p className="text-center text-xs text-slate-400 mt-4">
            Already have an account?{' '}
            <button 
              onClick={() => navigate('/login')}
              className="text-white underline hover:no-underline"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>

      {/* Expiry notice */}
      {reviewData.expires_at && (
        <p className="text-center text-sm text-slate-500 mt-4">
          This draft will be available for 1 hour. Sign up to save it permanently.
        </p>
      )}
    </div>
  );
};

export default GatedReviewDisplay;
